import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import {
    TrashIcon,
    EyeIcon,
    XCircleIcon,
} from 'lucide-react'
import { StatusBadge } from './StatusBadge'
import { JobCard } from './JobCard'
import { useNavigate } from "react-router";
import { SkeletonLoader } from '../../../common/SkeletonLoader';

export const JobsTable = ({
    filterStatus,
    searchQuery,
    onJobUpdated,
}) => {
    const [viewMode, setViewMode] = useState < 'table' | 'cards' > ('table')
    const [jobs, setJobs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchJobs = async () => {
            setIsLoading(true);
            try {
                const userId = Cookies.get("id");
                const jobRes = await axios.get('/api/jobprofile/', { withCredentials: true });
                const userJobs = jobRes.data.results.filter((job) => String(job.created_by) === String(userId));
                setJobs(userJobs);
            } catch (error) {
                console.error("Error while fetching jobs", error);
            } finally {
                setIsLoading(false);
            }
        }
        fetchJobs();
    }, []);

    const handleDeleteJob = async (jobId) => {
        const csrfToken = Cookies.get("csrftoken");
        try {
            await axios.delete(`/api/jobprofile/${jobId}/`, {
                withCredentials: true,
                headers: {
                    "X-CSRFToken": csrfToken,
                    "Content-Type": "application/json",
                },
            });
            setJobs((prevJobs) => prevJobs.filter((job) => job.id !== jobId));
            if (onJobUpdated) onJobUpdated();
        } catch (error) {
            console.error("Failed to delete job:", error);
        }
    }

    const handleCloseJob = async (jobId) => {
        const csrfToken = Cookies.get("csrftoken");
        try {
            await axios.patch(`/api/jobprofile/${jobId}/`, {
                is_active: false
            }, {
                withCredentials: true,
                headers: {
                    "X-CSRFToken": csrfToken,
                    "Content-Type": "application/json",
                },
            });
            setJobs((prevJobs) => prevJobs.map((job) => 
                job.id === jobId ? { ...job, is_active: false } : job
            ));
            if (onJobUpdated) onJobUpdated();
        } catch (error) {
            console.error("Failed to close job:", error);
        }
    }

    // Filter jobs based on status and search query
    const filteredJobs = jobs.filter((job) => {
        const matchesStatus =
            filterStatus === 'all' ||
            (filterStatus === 'active' && job.is_active) ||
            (filterStatus === 'closed' && !job.is_active)
        const matchesSearch =
            job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            job.country.toLowerCase().includes(searchQuery.toLowerCase()) ||
            job.city.toLowerCase().includes(searchQuery.toLowerCase())
        return matchesStatus && matchesSearch
    })
    return (
        <div>
            <div className="flex justify-end mb-4">
                <div className="inline-flex rounded-md shadow-sm">
                    <button
                        onClick={() => setViewMode('table')}
                        className={`px-4 py-2 text-sm font-medium rounded-l-md border ${viewMode === 'table' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                    >
                        Table View
                    </button>
                    <button
                        onClick={() => setViewMode('cards')}
                        className={`px-4 py-2 text-sm font-medium rounded-r-md border ${viewMode === 'cards' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                    >
                        Card View
                    </button>
                </div>
            </div>
            {isLoading ? (
                <div className="flex flex-col gap-y-4">
                    {Array.from({ length: 3 }).map((_, idx) => (
                        <SkeletonLoader key={idx} />
                    ))}
                </div>
            ) : viewMode === 'table' ? (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                        <thead className="bg-gray-50 dark:bg-gray-900">
                            <tr>
                                <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                                >
                                    Job
                                </th>
                                <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                                >
                                    Location
                                </th>
                                <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                                >
                                    Status
                                </th>
                                <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                                >
                                    Posted
                                </th>
                                {/* <th
                                    scope="col"
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                                >
                                    Applicants
                                </th> */}
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {filteredJobs.length > 0 ? (
                                filteredJobs.map((job) => (
                                    <tr key={job.id}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div>
                                                <div className="font-medium text-gray-900 dark:text-gray-100">
                                                    {job.title}
                                                </div>
                                                {/* <div className="text-sm text-gray-500">
                                                    {job.department}
                                                </div> */}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                            {`${job.city || "City"}, ${job.country || "Country"}`}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <StatusBadge status={job.is_active ? 'Active' : 'Closed'} />
                                        </td>

                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                            {new Date(job.created_at).toLocaleDateString('en-GB')}
                                        </td>
                                        {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                            {job.applicants}
                                        </td> */}
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex items-center justify-end space-x-2">
                                                <button className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300" onClick={() => navigate(`/jobpost/${job.id}`)}>
                                                    {job.is_active && (<EyeIcon className="h-5 w-5" />)}
                                                </button>
                                                {job.is_active && (
                                                    <button onClick={() => handleCloseJob(job.id)} className="text-orange-500 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300" title="Close Job">
                                                        <XCircleIcon className="h-5 w-5" />
                                                    </button>
                                                )}
                                                {/* <button className="text-gray-600 hover:text-gray-900">
                                                    <EditIcon className="h-5 w-5" />
                                                </button> */}
                                                <button onClick={() => handleDeleteJob(job.id)} className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300">
                                                    <TrashIcon className="h-5 w-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan={7}
                                        className="px-6 py-4 text-center text-gray-500 dark:text-gray-400"
                                    >
                                        No jobs found matching your criteria.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {filteredJobs.length > 0 ? (
                        filteredJobs.map((job) => <JobCard key={job.id} job={job} />)
                    ) : (
                        <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">
                            No jobs found matching your criteria.
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
